import Controller from "@/controllers/index.js";

// #region Factories

/**
 * @summary Cria objeto vazio do Modulo de WindTurbine
 */
const EmptyWindTurbineStateFactory = function()
{
	return {
		turbine:             EmptyWindTurbineFactory(),
		trendSignalSettings: [],
	};
};

/**
 * @summary Cria um view vazia de WindTurbine.
 * @returns {object} View de WindTurbine vazia.
 */
const EmptyWindTurbineFactory = function()
{
	return {
		monitoringElementId:                   0,
		name:                                  "",
		powerTrendSignalSettingId:             0,
		windSpeedTrendSignalSettingId:         0,
		phaseReferenceTrendSignalSettingId:    0,
		connectedProtocolTrendSignalSettingId: 0,
		ratedPower:                            0,
		latitude:                              0,
		longitude:                             0,
		minWindSpeed:                          0,
		maxWindSpeed:                          0,
		mainBearingsAvailable:                 0
	};
};

/**
 * @summary Cria um objeto do tipo WindTurbineView
 * @param {EmptyWindTurbineFactory} windTurbine View do backend preenchida.
 * @returns View da Wind Turbine.
 */
const WindTurbineFactory = function(windTurbine = EmptyWindTurbineFactory())
{
	return {
		monitoringElementId:                   windTurbine.monitoringElementId,
		name:                                  windTurbine.name,
		powerTrendSignalSettingId:             windTurbine.powerTrendSignalSettingId,
		windSpeedTrendSignalSettingId:         windTurbine.windSpeedTrendSignalSettingId,
		phaseReferenceTrendSignalSettingId:    windTurbine.phaseReferenceTrendSignalSettingId,
		connectedProtocolTrendSignalSettingId: windTurbine.connectedProtocolTrendSignalSettingId,
		ratedPower:                            windTurbine.ratedPower,
		latitude:                              windTurbine.latitude,
		longitude:                             windTurbine.longitude,
		minWindSpeed:                          windTurbine.minWindSpeed,
		maxWindSpeed:                          windTurbine.maxWindSpeed,
		mainBearingsAvailable:                 windTurbine.mainBearingsAvailable
	};
};

/**
 * Função que cria um objeto modelo que consta: Nome do ativo, Faixa de operação, Componente, Subcomponente,
 * Mensagem da notificação, Ocorrências, Agrupamento de notificações.
 * @returns View vazia dos modos de falhas.
 */
const EmptyNotificationsLogsFactory = function()
{
	  return {
		assetName:      "",
		operatingRange: -1,
		component:      -1,
		subComponent:   -1,
		message:        "",
		occurrences:    0,
		notifications:  [],
	  };
};

/**
 * Função que cria um objeto modelo que consta: Nome do ativo, Faixa de operação, Componente do ativo, Subcomponente,
 * Mensagem da notificação, Ocorrências, Agrupamento de notificações.
 * @param view - View de Notificações.
 * @returns View preenchida de Notificações preenchida com os valores atribuidos.
*/
const NotificationsLogsFactory = function(view = EmptyNotificationsLogsFactory())
{
	return {
		assetName:      view.name,
		operatingRange: view.operatingRange,
		component:      view.componentType,
		subComponent:   view.subComponentType,
		message:        view.message,
		occurrences:    view.occurrences,
		notifications:  view.notifications,
	};
};

/**
 * Função que cria um objeto modelo dos parametros construtivos.
 * @returns View vazia das configurações construtivas.
 */
const EmptyKinematicConfigurationFactory = function()
{
	return {
		name:                "",
		effectiveSince:      "",
		monitoringElementId: -1,
		gearbox:             {
			manufacturer: "",
			model:        "",
			type:         "",
			ratio:        0,
			numOfStages:  0,
			stages:       [ {
				name:   "",
				type:   0,
				shafts: [ {
					name:           "",
					rotorOrder:     0,
					gear:           { numTeeth: 0, type: "" },
					referenceShaft: false,
					bearings:       []
				} ]
			} ],
		},
		turbine: {
			manufacturer:    "",
			model:           "",
			ratedPower:      0,
			ratedWindSpeed:  0,
			cutInWindSpeed:  0,
			cutOutWindSpeed: 0,
			mainBearings:    [],
			generators:      [ {
				name:       "",
				numOfPoles: 0,
				bearings:   []
			} ],
		}
	};
};

// #endregion

// #region API Calls

/**
* Obtém informações de uma turbina.
* @param {number} id Id do elemento de monitoração da turbina.
* @returns {object} um objeto com as informações da turbina.
*/
const Get = async function(id = 0)
{
	const response = await Controller.WindTurbines.Get(id);
	if (response.status === 204) return {};

	return WindTurbineFactory(response.data);
};

/**
* @summary Obtém um conjunto de equipamentos de uma turbina.
* @param {number} monitoringElementId Id do elemento de monitoração da turbina.
* @returns {array} Array com os equipamentos de uma turbina.
*/
const GetWindTurbineEquipments = async(monitoringElementId) =>
{
	const res = await Controller.WindTurbines.GetWindTurbineEquipments(monitoringElementId);

	return res.data;
};

/**
* @summary Obtém um conjunto de ids dos equipamentos de uma turbina.
* @param {number} monitoringElementId Id do elemento de monitoração da turbina.
* @returns {array} Array com os ids dos equipamentos de uma turbina.
*/
const GetWindTurbineEquipmentsIds = async(monitoringElementId) =>
{
	const windTurbineEquipments = await GetWindTurbineEquipments(monitoringElementId);
	const equipmentsIds = [];

	windTurbineEquipments.forEach((windTurbineEquipment) =>
	{
		equipmentsIds.push(windTurbineEquipment.monitoringElementId);
	});

	return equipmentsIds;
};

/**
 * @summary Insere um novo evento de manutenção com arquivos anexos.
 * @param {object} parameters Objeto de parâmetros.
 */
const CreateEvent = async function(parameters)
{
	const resp = await Controller.WindTurbines.CreateEvent(parameters);
	return resp.data;
};

/**
 * @summary Busca os eventos de manutenção cadastrados para esta turbina em um período.
 * @param {object} parameters Objeto de parâmetros.
 */
const GetEvents = async function(parameters)
{
	const resp = await Controller.WindTurbines.GetEvents(parameters);
	return resp.data;
};

/**
 * @summary os eventos de manutenção mais recentes cadastrados para esta turbina.
 * @param {object} parameters Objeto de parâmetros.
 */
const GetLatestEvents = async function(parameters)
{
	const resp = await Controller.WindTurbines.GetLatestEvents(parameters);
	if (resp.status === 204) return [];

	return resp.data;
};

/**
 * @summary Obtém as notificações ocorridas para a unidade geradora.
 * @param notificationsParams - Parâmetros para solicitação com data e id.
 * @returns {NotificationsLogsFactory} Notificações geradas no período.
*/
const GetNotificationsLogs = async function(notificationsParams = {
	monitoringElementId: 0,
	startDateTime:       new Date(),
	endDateTime:         new Date(),
})
{
	const resp = await Controller.WindTurbines.GetNotificationsLogs(
		notificationsParams.monitoringElementId,
		notificationsParams.startDateTime,
		notificationsParams.endDateTime
	);

	if (resp.status === 204) return [];

	const windNotifications = resp.data.map((notifications) =>
	{
		return NotificationsLogsFactory(notifications);
	});
	return windNotifications;
};

// #endregion

export default {
	EmptyWindTurbineFactory,
	EmptyWindTurbineStateFactory,
	WindTurbineFactory,
	Get,
	GetWindTurbineEquipments,
	GetWindTurbineEquipmentsIds,
	CreateEvent,
	GetEvents,
	GetLatestEvents,
	GetNotificationsLogs,
};